/**
 * @fileoverview Admin — Edit History / Audit Log viewer.
 * Shows field-level changes for AcademyPlayer, AcademyCoach with filters.
 */

import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { academyService } from "api";
import FootballLoader from "components/FootballLoader";

const ENTITY_OPTIONS = [
  { value: "", label: "All Sections" },
  { value: "AcademyPlayer", label: "Players" },
  { value: "AcademyCoach", label: "Coaches" },
];

const ACTION_COLOR = { Created: "success", Updated: "info", Deleted: "error" };

const TODAY = new Date().toISOString().slice(0, 10);
const WEEK_AGO = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);

function AuditRow({ log }) {
  return (
    <SoftBox display="flex" alignItems="flex-start" py={1.5} px={2}
      sx={{ borderBottom: "1px solid #f5f5f5", gap: 2, flexWrap: "wrap" }}>
      {/* Time */}
      <SoftBox flex="0 0 130px">
        <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
          {new Date(log.changedAt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
        </SoftTypography>
      </SoftBox>
      {/* Section */}
      <SoftBox flex="0 0 100px">
        <SoftTypography variant="caption" fontWeight="medium" color="dark">
          {log.entityName === "AcademyPlayer" ? "Player" : log.entityName === "AcademyCoach" ? "Coach" : log.entityName}
        </SoftTypography>
      </SoftBox>
      {/* Action */}
      <SoftBox flex="0 0 80px">
        <SoftBadge badgeContent={log.action} color={ACTION_COLOR[log.action] ?? "info"} size="xs" container />
      </SoftBox>
      {/* Field + Change */}
      <SoftBox flex="1" sx={{ minWidth: 0 }}>
        {log.fieldName ? (
          <SoftBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <Chip label={log.fieldName} size="small" sx={{ fontSize: "0.68rem", height: 20 }} />
            {log.oldValue && (
              <SoftTypography variant="caption" color="error" sx={{ textDecoration: "line-through" }}>
                {log.oldValue}
              </SoftTypography>
            )}
            {log.oldValue && log.newValue && (
              <Icon sx={{ fontSize: "0.9rem", color: "text.secondary" }}>arrow_forward</Icon>
            )}
            {log.newValue && (
              <SoftTypography variant="caption" color="success">{log.newValue}</SoftTypography>
            )}
          </SoftBox>
        ) : (
          <SoftTypography variant="caption" color="text">{log.action}</SoftTypography>
        )}
      </SoftBox>
      {/* Changed by */}
      <SoftBox flex="0 0 160px" textAlign="right" sx={{ minWidth: 0 }}>
        <SoftTypography variant="caption" color="text"
          sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
          {log.changedBy ?? "System"}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}
AuditRow.propTypes = {
  log: PropTypes.object.isRequired,
};

function AuditLogs() {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [hasLoaded, setHasLoaded]   = useState(false);
  const [entityName, setEntityName] = useState("");
  const [from, setFrom]             = useState(WEEK_AGO);
  const [to, setTo]                 = useState(TODAY);
  const [page, setPage]             = useState(1);

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const data = await academyService.getAuditLogs({
        entityName: entityName || undefined,
        from: from || undefined,
        to: to || undefined,
        page: pg, pageSize: 100,
      });
      setLogs(Array.isArray(data) ? data : []);
      setPage(pg);
      setHasLoaded(true);
    } catch (err) {
      console.error(err);
      setLogs([]);
    } finally { setLoading(false); }
  }, [entityName, from, to]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Edit History</SoftTypography>
            <SoftTypography variant="body2" color="text">
              Field-level change tracking for players and coaches
            </SoftTypography>
          </SoftBox>
        </SoftBox>

        <Card>
          {/* Filters */}
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
            <TextField select size="small" label="Section" value={entityName}
              onChange={(e) => setEntityName(e.target.value)} sx={{ minWidth: 150 }}>
              {ENTITY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="From" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
            <TextField size="small" label="To" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
            <SoftButton variant="gradient" color="info" size="small" onClick={() => load(1)}>
              <Icon sx={{ mr: 0.5 }}>search</Icon> Search
            </SoftButton>
            {hasLoaded && (
              <SoftTypography variant="caption" color="text" ml="auto">
                {logs.length} records
              </SoftTypography>
            )}
          </SoftBox>

          {/* Column headers */}
          <SoftBox display="flex" alignItems="center" px={2} py={1}
            sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa", flexWrap: "wrap", gap: 2 }}>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 130px" }}>TIMESTAMP</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 100px" }}>SECTION</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px" }}>ACTION</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>CHANGE</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 160px", textAlign: "right" }}>CHANGED BY</SoftTypography>
          </SoftBox>

          {loading ? (
            <FootballLoader label="Loading history…" />
          ) : !hasLoaded ? (
            <SoftBox p={4} textAlign="center">
              <Icon sx={{ fontSize: "2rem", color: "text.secondary" }}>history</Icon>
              <SoftTypography variant="caption" color="text" display="block" mt={1}>
                Set filters and press Search to view edit history
              </SoftTypography>
            </SoftBox>
          ) : logs.length === 0 ? (
            <SoftBox p={4} textAlign="center">
              <SoftTypography variant="caption" color="text">No history records found for the selected criteria</SoftTypography>
            </SoftBox>
          ) : (
            <>
              {logs.map((log) => <AuditRow key={log.id} log={log} />)}
              {logs.length === 100 && (
                <SoftBox p={2} display="flex" justifyContent="center">
                  <SoftButton variant="outlined" color="info" size="small" onClick={() => load(page + 1)}>
                    Load more
                  </SoftButton>
                </SoftBox>
              )}
            </>
          )}
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />
    </DashboardLayout>
  );
}

export default AuditLogs;
